import { Badge, Logo } from '@auctisync/ui';
import { useEffect, useRef, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { AdminNav } from '../../components/AdminNav';
import { SignOutButton } from '../../components/SignOutButton';
import { Copilot } from '../../components/copilot/Copilot';
import { readClaims, useSession } from '../../hooks/useSession';

/**
 * Layout for every AUTHENTICATED tenant-admin page. Renders a left sidebar
 * with AuctiSync branding + section nav + sign-out; the <Outlet /> fills the
 * main content region.
 *
 * Auth pages (sign-in, forgot password) skip this layout and use the bare
 * AdminLayout to stay focused.
 *
 * RESPONSIVE CONTRACT — the sidebar column is 16rem and does not compress. On a
 * phone that left ~137px for the entire page, so the grid grew wider than the
 * screen and every admin page scrolled sideways with the canvas showing as a
 * grey stripe beside the sidebar. Below `md` the sidebar is therefore taken out
 * of flow and driven as an off-canvas drawer, and the grid collapses to one
 * column.
 *
 * `min-w-0` on <main> is load-bearing, not cosmetic: a grid item defaults to
 * `min-width: auto`, meaning it refuses to shrink below its content's
 * min-content width. Without it the inner `overflow-x: auto` table wrappers
 * widen the column instead of scrolling inside it.
 *
 * The breakpoint deliberately lives ONLY in CSS. An earlier revision tracked it
 * in React via matchMedia and drove the drawer's `inert` from that state; a
 * resize that arrived without a `change` event left the flag stale and put the
 * entire nav out of keyboard reach on desktop. Here JS knows only open/closed,
 * and `invisible md:visible` removes the off-canvas links from the tab order —
 * a rule that cannot go out of sync with the rendered layout.
 */

export function AdminChromeLayout() {
  const { session } = useSession();
  const { appRole, tenantId } = readClaims(session);
  const [navOpen, setNavOpen] = useState(false);
  const { pathname } = useLocation();
  const openerRef = useRef<HTMLButtonElement | null>(null);
  const drawerRef = useRef<HTMLElement | null>(null);

  // Tapping a nav link must dismiss the drawer, or the next page renders behind
  // an open overlay.
  useEffect(() => {
    setNavOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!navOpen) return;
    function onKey(event: KeyboardEvent): void {
      if (event.key === 'Escape') setNavOpen(false);
    }
    // Rotating or crossing the breakpoint turns the drawer back into a static
    // column; the body scroll lock below must not survive that.
    function onResize(): void {
      setNavOpen(false);
    }
    const opener = openerRef.current;
    const previousOverflow = document.body.style.overflow;
    window.addEventListener('keydown', onKey);
    window.addEventListener('resize', onResize);
    document.body.style.overflow = 'hidden';
    drawerRef.current?.focus();
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onResize);
      document.body.style.overflow = previousOverflow;
      opener?.focus();
    };
  }, [navOpen]);

  return (
    <div
      data-theme="admin"
      className="grid min-h-screen grid-cols-1 md:grid-cols-[16rem_1fr]"
      style={{ background: 'var(--canvas-bg, var(--surface))' }}
    >
      {/* Phone-only chrome: the drawer needs a handle, and the brand should not
          disappear just because the sidebar slid away. */}
      <header
        className="sticky top-0 z-20 flex items-center gap-3 px-4 py-3 md:hidden"
        style={{
          background: 'var(--sidebar-band, var(--surface-elev))',
          borderBottom: '1px solid var(--admin-hairline, var(--border))',
        }}
      >
        <button
          ref={openerRef}
          type="button"
          onClick={() => setNavOpen(true)}
          aria-label="Open admin navigation"
          aria-expanded={navOpen}
          aria-controls="admin-drawer"
          className="flex h-10 w-10 items-center justify-center rounded-md"
          style={{ border: '1px solid var(--admin-hairline, var(--border))', color: 'var(--text)' }}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <path d="M2 4.5h14M2 9h14M2 13.5h14" />
          </svg>
        </button>
        <Link
          to="/admin/dashboard"
          style={{ display: 'flex', alignItems: 'center', color: 'var(--text)' }}
        >
          <Logo height={20} />
        </Link>
      </header>

      {navOpen && (
        <div
          className="fixed inset-0 z-30 md:hidden"
          style={{ background: 'rgba(15, 23, 42, 0.4)' }}
          onClick={() => setNavOpen(false)}
          aria-hidden="true"
        />
      )}

      <aside
        id="admin-drawer"
        ref={drawerRef}
        tabIndex={-1}
        aria-label="Admin navigation"
        className={[
          'fixed top-0 bottom-0 left-0 z-40 flex w-64 flex-col overflow-y-auto px-4 py-6',
          // `visibility` is what keeps the off-canvas links out of the tab
          // order, and it is deliberately NOT transitioned: CSS holds an
          // element visible for the whole duration of a visibility transition,
          // so animating it would make keyboard access depend on an animation
          // running to completion. Only `transform` animates.
          'transition-transform duration-200 ease-out',
          navOpen ? 'visible translate-x-0' : 'invisible -translate-x-full',
          'md:visible md:sticky md:bottom-auto md:z-auto md:h-screen md:translate-x-0 md:transition-none',
        ].join(' ')}
        style={{
          borderRight: '1px solid var(--admin-hairline, var(--border))',
          background: 'var(--sidebar-band, var(--surface-elev))',
        }}
      >
        <div style={{ marginBottom: '1.5rem' }}>
          <div className="flex items-center justify-between gap-2">
            <Link
              to="/admin/dashboard"
              style={{
                display: 'flex',
                alignItems: 'center',
                color: 'var(--text)',
                textDecoration: 'none',
              }}
            >
              <Logo height={22} />
            </Link>
            <button
              type="button"
              onClick={() => setNavOpen(false)}
              aria-label="Close admin navigation"
              className="flex h-8 w-8 items-center justify-center rounded-md md:hidden"
              style={{ color: 'var(--text-muted)' }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                aria-hidden="true"
              >
                <path d="M3 3l10 10M13 3L3 13" />
              </svg>
            </button>
          </div>
          <div style={{ marginTop: '0.25rem', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
            Admin
          </div>
        </div>

        <div style={{ flex: 1 }}>
          <AdminNav />
        </div>

        <div
          style={{
            marginTop: '1.5rem',
            paddingTop: '1rem',
            borderTop: '1px solid var(--admin-hairline, var(--border))',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.5rem',
            fontSize: '0.75rem',
            color: 'var(--text-muted)',
          }}
        >
          <div style={{ wordBreak: 'break-all' }}>{session?.user.email ?? '—'}</div>
          <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
            <Badge variant="outline">{appRole ?? 'no role'}</Badge>
            {tenantId && <Badge variant="outline">tenant {tenantId.slice(0, 6)}…</Badge>}
          </div>
          <Link
            to="/auth/reset-password"
            style={{ color: 'var(--text-muted)', fontSize: '0.75rem', textDecoration: 'underline' }}
          >
            Change password
          </Link>
          <SignOutButton redirectTo="/admin" />
        </div>
      </aside>

      <main className="w-full max-w-7xl min-w-0 p-4 md:px-10 md:py-8">
        <Outlet />
      </main>

      <Copilot />
    </div>
  );
}
